import { inject, injectable } from 'inversify';
import { type AuthTokenProviderInterface } from './interfaces';
import {
  AuthError,
  type Config,
  isNotBlank,
  type Logger,
  TokenTypes,
  TYPES,
} from '@bizzdesign/sdk-internal';
import { fetchAuthSession } from 'aws-amplify/auth';

@injectable()
export class CognitoAuthorizationCodeFlowProvider
  implements AuthTokenProviderInterface
{
  constructor(
    @inject(TYPES.Logger) private readonly logger: Logger,
    @inject(TYPES.Config) private readonly config: Config,
  ) {}

  getToken(): Promise<string> {
    if (this.config.useTokenType === TokenTypes.ACCESS_TOKEN) {
      return this.getAccessToken();
    } else {
      return this.getIdToken();
    }
  }

  async getIdToken(): Promise<string> {
    this.logger.debug({
      context: 'AuthTokenProvider',
      msg: 'Retrieving ID token',
    });
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (isNotBlank(idToken)) {
        return idToken;
      }
      throw new AuthError('ID Token is not available in the current session');
    } catch (error) {
      this.logger.error({
        context: 'AuthTokenProvider',
        msg: 'Error thrown while retrieving the ID Token',
        error: error,
      });
      throw new AuthError('ID Token could not be retrieved', error);
    }
  }

  async getAccessToken(): Promise<string> {
    this.logger.debug({
      context: 'AuthTokenProvider',
      msg: 'Retrieving Access token',
    });
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (isNotBlank(accessToken)) {
        return accessToken;
      }
      throw new AuthError(
        'Access Token is not available in the current session',
      );
    } catch (error) {
      this.logger.error({
        context: 'AuthTokenProvider',
        msg: 'Error thrown while retrieving the Access Token',
        error: error,
      });
      throw new AuthError('Access Token could not be retrieved', error);
    }
  }
}
